import React, { useState } from "react";
import {
  Row,
  Col,
  Container,
  Form,
  Button,
  Alert,
  Spinner,
} from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";

// Import selectors & action from setting store
import * as SettingSelector from "../../../../store/setting/selectors";
// Redux Selector / Action
import { useSelector } from "react-redux";

// Import auth service
import { authService } from "../../../../services/api";

//img
import rsLogo from "../../../../assets/images/rs_logo.png";

const ChangePassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const appName = useSelector(SettingSelector.app_name);

  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertVariant, setAlertVariant] = useState("danger");
  const [validated, setValidated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Check if user was redirected due to password change requirement
  const isPasswordChangeRequired =
    location.state?.passwordChangeRequired || false;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validatePasswords = () => {
    if (formData.newPassword !== formData.confirmPassword) {
      setAlertVariant("danger");
      setAlertMessage("New passwords do not match");
      setShowAlert(true);
      return false;
    }

    if (formData.newPassword.length < 8) {
      setAlertVariant("danger");
      setAlertMessage("New password must be at least 8 characters long");
      setShowAlert(true);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    if (!validatePasswords()) {
      return;
    }

    setIsLoading(true);

    try {
      // Call the API to change password
      await authService.changePassword(
        formData.currentPassword,
        formData.newPassword,
      );

      // Successful password change
      setAlertVariant("success");
      setAlertMessage(
        "Password changed successfully! Redirecting to dashboard...",
      );
      setShowAlert(true);

      // Clear RouteGuard cache to ensure fresh validation after password change
      if (typeof window !== "undefined" && window.routeGuardClearCache) {
        window.routeGuardClearCache();
      }

      // Redirect after a short delay
      setTimeout(() => {
        navigate("/portal/summary");
      }, 2000);
    } catch (error) {
      // Failed password change
      setAlertVariant("danger");
      setAlertMessage(
        error.response?.data?.message ||
          "Failed to change password. Please check your current password and try again.",
      );
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <section className="sign-in-page">
        <Container fluid>
          <Row className="align-items-center">
            <Col md={6} className="overflow-hidden position-relative">
              <div className="bg-primary w-100 h-100 position-absolute top-0 bottom-0 start-0 end-0"></div>
              <div className="container-inside z-1">
                <div className="main-circle circle-small"></div>
                <div className="main-circle circle-medium"></div>
                <div className="main-circle circle-large"></div>
                <div className="main-circle circle-xlarge"></div>
                <div className="main-circle circle-xxlarge"></div>
              </div>
              <div className="sign-in-detail container-inside-top">
                <div className="text-center text-white">
                  <h1 className="mb-3 text-white fw-semibold">
                    Security First
                  </h1>
                  <p className="font-size-16 text-white mb-0">
                    Keep your account secure by regularly updating your
                    password.
                    <br /> Make it strong, unique, and memorable only to you.
                  </p>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="sign-in-from text-center">
                <Link
                  to="/"
                  className="d-inline-flex align-items-center justify-content-center gap-2"
                >
                  <img
                    src={rsLogo}
                    alt="RS Logo"
                    width="50"
                    className="img-fluid"
                  />
                  <h2 className="logo-title" data-setting="app_name">
                    {appName}
                  </h2>
                </Link>

                {isPasswordChangeRequired && (
                  <Alert variant="warning" className="mb-4 mt-3">
                    <strong>Password Change Required</strong>
                    <br />
                    For security reasons, you must change your password before
                    continuing.
                  </Alert>
                )}

                {showAlert && (
                  <Alert
                    variant={alertVariant}
                    className="mb-4"
                    onClose={() => setShowAlert(false)}
                    dismissible
                  >
                    {alertMessage}
                  </Alert>
                )}

                <h4 className="mt-3 mb-4">Change Your Password</h4>

                <Form
                  className="mt-3"
                  noValidate
                  validated={validated}
                  onSubmit={handleSubmit}
                >
                  <Form.Group className="form-group text-start">
                    <h6 className="form-label fw-bold">Current Password</h6>
                    <Form.Control
                      type="password"
                      className="form-control mb-0"
                      placeholder="Enter your current password"
                      name="currentPassword"
                      value={formData.currentPassword}
                      onChange={handleChange}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      Please enter your current password
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="form-group text-start">
                    <h6 className="form-label fw-bold">New Password</h6>
                    <Form.Control
                      type="password"
                      className="form-control mb-0"
                      placeholder="Enter your new password"
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleChange}
                      required
                      minLength={8}
                    />
                    <Form.Control.Feedback type="invalid">
                      New password must be at least 8 characters long
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="form-group text-start">
                    <h6 className="form-label fw-bold">Confirm New Password</h6>
                    <Form.Control
                      type="password"
                      className="form-control mb-0"
                      placeholder="Confirm your new password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      minLength={8}
                    />
                    <Form.Control.Feedback type="invalid">
                      Please confirm your new password
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Button
                    variant="primary"
                    type="submit"
                    className="btn btn-primary mt-4 fw-semibold text-uppercase w-100"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Changing Password...
                      </>
                    ) : (
                      "Change Password"
                    )}
                  </Button>

                  {!isPasswordChangeRequired && (
                    <div className="text-center mt-4">
                      <Link to="/portal/summary" className="text-muted">
                        Skip for now
                      </Link>
                    </div>
                  )}
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default ChangePassword;
